import { useCallback } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Link } from '@mui/material';

import { AccountScreen } from 'common/accountActivationAndRecovery/AccountScreen';
import { selectIsSwitchingToSiteUser, selectRespondentTarget } from 'common/deviceHandoff/state/selectors';
import { Loading } from 'common/utils/loading';
import { LoginForm } from 'module/auth/login/components/LoginForm/LoginForm';

import { deviceHandoffScreenPath } from '../../deviceHandoffScreenPaths';

import { ActivatePatientButton } from './components/ActivatePatientButton';
import { useDeviceHandoffLoginSubmit, useHandoffLoginFeedback, useShowPageStatusOnRespondentFinished } from './hooks';

export const DeviceHandoffLoginScreen = (): ReactElement => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const isSwitchingToSiteUser = useSelector(selectIsSwitchingToSiteUser);
    const respondentTarget = useSelector(selectRespondentTarget);

    const { handleAuthRejectionFeedback } = useHandoffLoginFeedback();

    useShowPageStatusOnRespondentFinished();
    const { handleSubmit, isDeviceHandoffLoginSubmitLoading } = useDeviceHandoffLoginSubmit({
        onAuthComplete: handleAuthRejectionFeedback,
    });

    const goToActivation = useCallback(
        () => navigate(deviceHandoffScreenPath(AccountScreen.Activation), { replace: true }),
        [navigate]
    );
    const goToForgotUser = useCallback(
        () => navigate(deviceHandoffScreenPath(AccountScreen.ForgotUser), { replace: true }),
        [navigate]
    );

    if (isSwitchingToSiteUser) {
        return <Loading isLoading />;
    }

    const isRespondentActivated = respondentTarget?.activationStatus;

    return (
        <LoginForm
            isLoading={isDeviceHandoffLoginSubmitLoading}
            onSubmit={handleSubmit}
            header={t('login.switch_log_in')}
            className="switch_user_login_page"
            prefilledUsername={respondentTarget?.respondentID}
            footer={
                <>
                    {!isRespondentActivated && <ActivatePatientButton onClick={goToActivation} />}
                    {/* auth-link classname comes from styles in LoginForm */}
                    <Link component="button" className="auth-link" onClick={goToForgotUser}>
                        {t('login.forgotbutton')}
                    </Link>
                </>
            }
        />
    );
};
